// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import textMasks from "@mcmc/App/Libraries/react-text-mask";
import { FormField, FormState, ValidatorName } from "@mcmc/Overrides/Widgets/CreateAccount/CreateAccount.form-helper";

const defaultMaxLength = 100;
const requiredValidators: ValidatorName[] = ["required"];
const postalCodeValidators: ValidatorName[] = ["required", "zipCode"];

export interface EventRegistrationFormState extends FormState {
    fields: {
        firstName: FormField<string>;
        lastName: FormField<string>;
        title: FormField<string>;
        company: FormField<string>;
        streetAddress: FormField<string>;
        addressContinued: FormField<string>;
        city: FormField<string>;
        country: FormField<number>;
        state: FormField<string>;
        zipCode: FormField<string>;
        businessPhone: FormField<string>;
        mobilePhone: FormField<string>;
        faxNumber: FormField<string>;
        email: FormField<string>;
        howCanWeContactYou: FormField<string>;
        comments: FormField<string>;
        paymentMethod: FormField<string>;
        poNumber: FormField<string>;
    };
}

export const initailEventRegistrationFormState: EventRegistrationFormState = {
    isValid: false,
    isSubmitted: false,
    fields: {
        firstName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "event_reg_firstName",
                label: translate("First Name"),
                required: true,
                maxLength: 40,
            },
        },
        lastName: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "event_reg_lastName",
                label: translate("Last Name"),
                required: true,
                maxLength: 40,
            },
        },
        title: {
            value: "",
            attrs: {
                id: "event_reg_title",
                label: translate("Title (optional)"),
                maxLength: defaultMaxLength,
            },
        },
        company: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "event_reg_company",
                label: translate("Company"),
                required: true,
                maxLength: defaultMaxLength,
            },
        },
        streetAddress: {
            value: "",
            validators: requiredValidators,
            attrs: {
                id: "event_reg_streetAddress",
                label: translate("Street Address"),
                required: true,
                maxLength: 40,
            },
        },
        addressContinued: {
            value: "",
            attrs: {
                id: "event_reg_addressContinued",
                label: translate("Address (Continued)"),
                maxLength: 40,
            },
        },
        city: {
            value: "",
            validators: requiredValidators,
            attrs: {
                id: "event_reg_city",
                label: translate("City"),
                required: true,
                maxLength: 30,
            },
        },
        country: {
            value: 0,
            validators: requiredValidators,
            attrs: {
                id: "event_reg_country",
                label: translate("Country"),
                required: true,
            },
        },
        state: {
            value: "",
            validators: requiredValidators,
            attrs: {
                id: "event_reg_state",
                label: translate("State/Province"),
                required: true,
            },
        },
        zipCode: {
            value: "",
            validators: postalCodeValidators,
            mask: textMasks.postalCode,
            attrs: {
                id: "event_reg_postalCode",
                label: translate("Zip/Postal Code"),
                required: true,
            },
        },
        businessPhone: {
            value: "",
            validators: ["required", "phone"],
            mask: textMasks.phone,
            attrs: {
                id: "event_reg_businessPhone",
                label: translate("Business Phone"),
                required: true,
            },
        },
        mobilePhone: {
            value: "",
            validators: ["phone"],
            mask: textMasks.phone,
            attrs: {
                id: "event_reg_mobilePhone",
                label: translate("Contact/Mobile Phone (Optional)"),
            },
        },
        faxNumber: {
            value: "",
            attrs: {
                id: "event_reg_faxNumber",
                label: translate("Fax (Optional)"),
                maxLength: 40,
            },
        },
        email: {
            value: "",
            validators: ["required", "email"],
            attrs: {
                type: "email",
                id: "event_reg_email",
                label: translate("Email"),
                required: true,
                maxLength: 256,
            },
        },
        howCanWeContactYou: {
            value: "",
            validators: requiredValidators,
            attrs: {
                id: "event_reg_howCanWeContactYou",
                label: translate("How can we contact you?"),
                required: true,
            },
        },
        comments: {
            value: "",
            attrs: {
                id: "event_reg_comments",
                label: translate("Comments, Special Requests or Dietary Restrictions?"),
                maxLength: 300,
            },
        },
        paymentMethod: {
            value: "",
            validators: requiredValidators,
            attrs: {
                id: "event_reg_paymentMethod",
                label: translate("Select Payment Method"),
                required: true,
            },
        },
        poNumber: {
            value: "",
            validators: ["required"],
            attrs: {
                id: "event_reg_poNumber",
                label: translate("PO #/Reference #"),
                required: true,
                maxLength: 50,
            },
        },
    },
};
